import { sql } from 'drizzle-orm';
import {
  pgTable,
  pgPolicy,
  check,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { communications } from './communications';
import { documents } from './documents';
import { agentActionTypeEnum, agentRunStatusEnum, agentTypeEnum, aiCorrectionFeatureEnum, aiCorrectionTypeEnum, aiFeedbackSourceTypeEnum, aiInteractionTypeEnum, aiReviewChangeTypeEnum, aiTrainingFeedbackCategoryEnum, perilTypeEnum } from './enums';
import { estimatePitfallPatterns, estimateSamples } from './financial';
import { organizations } from './organizations';
import { users } from './users';

// AI Reviews table for tracking biweekly claim strategy reviews
export const aiReviews = pgTable("ai_reviews", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Review content
  summary: text("summary").notNull(), // AI-generated summary of the review
  recommendedActions: jsonb("recommended_actions"), // Array of recommended actions
  changeType: aiReviewChangeTypeEnum("change_type").notNull().default("no_change"),
  
  // AI analysis details
  confidence: integer("confidence").default(75), // AI confidence in recommendations (0-100)
  analysisDetails: jsonb("analysis_details"), // Detailed AI reasoning and data sources analyzed
  
  // Review tracking
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
  reviewerId: varchar("reviewer_id").references(() => users.id), // PA who acknowledged/reviewed it
  acknowledgedAt: timestamp("acknowledged_at"), // When PA marked as reviewed
  acknowledgeNotes: text("acknowledge_notes"), // PA notes on the AI recommendation
  
  // Metadata
  dataSourcesAnalyzed: jsonb("data_sources_analyzed"), // Track what was analyzed (documents count, emails count, etc)
  
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_ai_reviews_claim_id").on(table.claimId),
  index("idx_ai_reviews_generated_at").on(table.generatedAt),
  index("idx_ai_reviews_change_type").on(table.changeType),
  index("idx_ai_reviews_acknowledged_at").on(table.acknowledgedAt),
]);


// AI Review Feedback - For continuous learning from adjuster feedback
export const reviewFeedback = pgTable("review_feedback", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  reviewId: uuid("review_id").references(() => aiReviews.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  
  // Feedback content
  rating: integer("rating").notNull(), // 1-5 star rating
  wasHelpful: boolean("was_helpful"), // Simple helpful/not helpful
  feedbackText: text("feedback_text"), // Optional detailed feedback
  
  // Which recommendations were followed
  actionsTaken: jsonb("actions_taken"), // Array of action indices that were followed
  actionsIgnored: jsonb("actions_ignored"), // Array of action indices that were ignored
  
  // Feedback categories
  accuracyRating: integer("accuracy_rating"), // How accurate was the analysis (1-5)
  relevanceRating: integer("relevance_rating"), // How relevant were recommendations (1-5)
  actionabilityRating: integer("actionability_rating"), // How actionable were recommendations (1-5)
  
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_review_feedback_review_id").on(table.reviewId),
  index("idx_review_feedback_user_id").on(table.userId),
  index("idx_review_feedback_rating").on(table.rating),
  index("idx_review_feedback_was_helpful").on(table.wasHelpful),
]);


// Strategy Outcomes - Track claim outcomes to learn what works
export const strategyOutcomes = pgTable("strategy_outcomes", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  reviewId: uuid("review_id").references(() => aiReviews.id),
  
  // Outcome details
  outcomeType: varchar("outcome_type").notNull(), // 'settled', 'increased_offer', 'appraisal', 'litigation', 'doi_complaint', 'withdrawn'
  settlementIncrease: decimal("settlement_increase", { precision: 12, scale: 2 }), // Amount of increase achieved
  timeToResolution: integer("time_to_resolution"), // Days from review to outcome
  
  // What strategy/actions led to this outcome
  strategiesUsed: jsonb("strategies_used"), // Array of strategies applied (from AI recommendations or manual)
  documentsAdded: jsonb("documents_added"), // Documents that were key to success
  communicationApproach: text("communication_approach"), // Description of communication strategy
  
  // Success metrics
  wasSuccessful: boolean("was_successful").notNull().default(false),
  successScore: integer("success_score"), // 1-100 score of how successful
  
  // Lessons learned
  lessonsLearned: text("lessons_learned"), // What worked well or didn't work
  wouldRecommendAgain: boolean("would_recommend_again"), // Would adjuster use this strategy again
  
  recordedBy: varchar("recorded_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_strategy_outcomes_claim_id").on(table.claimId),
  index("idx_strategy_outcomes_review_id").on(table.reviewId),
  index("idx_strategy_outcomes_type").on(table.outcomeType),
  index("idx_strategy_outcomes_was_successful").on(table.wasSuccessful),
  index("idx_strategy_outcomes_created_at").on(table.createdAt),
]);


// Learning Examples - Knowledge base of successful strategies for RAG
export const learningExamples = pgTable("learning_examples", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Example classification
  title: varchar("title", { length: 255 }).notNull(),
  category: varchar("category").notNull(), // 'rebuttal', 'negotiation', 'documentation', 'communication', 'appraisal', 'state_statute_violations'
  perilTypes: jsonb("peril_types"), // Array of applicable peril types
  claimStatuses: jsonb("claim_statuses"), // Array of applicable claim statuses
  
  // Example content
  situation: text("situation").notNull(), // Description of the situation
  strategyUsed: text("strategy_used").notNull(), // What strategy was employed
  outcome: text("outcome").notNull(), // What was the result
  keyLessons: jsonb("key_lessons"), // Array of key takeaways
  
  // Effectiveness tracking
  timesRetrieved: integer("times_retrieved").default(0), // How often this example was used
  timesHelpful: integer("times_helpful").default(0), // How often feedback indicated it was helpful
  effectivenessScore: decimal("effectiveness_score", { precision: 5, scale: 2 }), // Calculated effectiveness (0-100)
  
  // Source tracking
  sourceClaimId: uuid("source_claim_id").references(() => claims.id, { onDelete: "set null" }), // Original claim this came from
  sourceOutcomeId: uuid("source_outcome_id").references(() => strategyOutcomes.id), // Outcome record it's based on
  
  // Curation
  isApproved: boolean("is_approved").default(false), // Admin-approved for use
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  
  createdBy: varchar("created_by").references(() => users.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_learning_examples_category").on(table.category),
  index("idx_learning_examples_is_approved").on(table.isApproved),
  index("idx_learning_examples_effectiveness").on(table.effectivenessScore),
  index("idx_learning_examples_times_retrieved").on(table.timesRetrieved),
]);


// AI Agent System Tables
// Agent Runs - Track each execution of an AI agent
export const agentRuns = pgTable("agent_runs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Agent identification
  agentType: agentTypeEnum("agent_type").notNull(),
  agentId: varchar("agent_id").notNull(), // OpenAI Assistant ID
  threadId: varchar("thread_id"), // OpenAI Thread ID for conversation continuity
  
  // Context
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id), // User who triggered (null for automated runs)
  
  // Execution tracking
  status: agentRunStatusEnum("status").notNull().default("queued"),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  
  // Results
  summary: text("summary"), // Agent's summary of what it did
  findings: jsonb("findings"), // Structured findings (alerts, violations, recommendations)
  recommendations: jsonb("recommendations"), // Array of recommended actions
  
  // Approval workflow
  requiresApproval: boolean("requires_approval").default(false),
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  approvalNotes: text("approval_notes"),
  
  // Error handling
  errorMessage: text("error_message"),
  errorDetails: jsonb("error_details"),
  
  // Metrics
  tokensUsed: integer("tokens_used"),
  executionTimeMs: integer("execution_time_ms"),
  toolCallsCount: integer("tool_calls_count").default(0),
  
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_agent_runs_agent_type").on(table.agentType),
  index("idx_agent_runs_claim_id").on(table.claimId),
  index("idx_agent_runs_status").on(table.status),
  index("idx_agent_runs_started_at").on(table.startedAt),
  index("idx_agent_runs_requires_approval").on(table.requiresApproval),
]);


// Agent Actions - Individual actions taken by agents during a run
export const agentActions = pgTable("agent_actions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: uuid("run_id").references(() => agentRuns.id, { onDelete: "cascade" }).notNull(),
  
  // Action details
  actionType: agentActionTypeEnum("action_type").notNull(),
  actionName: varchar("action_name").notNull(), // Name of tool/function called
  actionInput: jsonb("action_input"), // Input parameters
  actionOutput: jsonb("action_output"), // Result of action
  
  // Decision tracking
  reasoning: text("reasoning"), // Why agent took this action
  confidence: integer("confidence"), // 0-100
  
  // Execution
  executedAt: timestamp("executed_at").defaultNow(),
  executionTimeMs: integer("execution_time_ms"),
  wasSuccessful: boolean("was_successful").default(true),
  errorMessage: text("error_message"),
  
  // Audit trail
  affectedEntityType: varchar("affected_entity_type"), // 'claim', 'task', 'document', 'timeline', etc.
  affectedEntityId: varchar("affected_entity_id"),
  
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_agent_actions_run_id").on(table.runId),
  index("idx_agent_actions_type").on(table.actionType),
  index("idx_agent_actions_executed_at").on(table.executedAt),
  index("idx_agent_actions_affected_entity").on(table.affectedEntityType, table.affectedEntityId),
]);


// Agent Configuration - Store agent settings and tool permissions
export const agentConfigurations = pgTable("agent_configurations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Agent identity
  agentType: agentTypeEnum("agent_type").notNull().unique(),
  agentId: varchar("agent_id"), // OpenAI Assistant ID (set when created)
  name: varchar("name").notNull(),
  description: text("description"),
  
  // Configuration
  isActive: boolean("is_active").default(true),
  model: varchar("model").default("gpt-4o").notNull(),
  instructions: text("instructions").notNull(), // System prompt
  
  // Tool permissions
  allowedTools: jsonb("allowed_tools").notNull(), // Array of tool names this agent can use
  allowedScopes: jsonb("allowed_scopes"), // What data scopes agent can access
  
  // Execution settings
  autoRunEnabled: boolean("auto_run_enabled").default(false), // Auto-run on schedule
  autoRunSchedule: varchar("auto_run_schedule"), // Cron expression
  requiresHumanApproval: boolean("requires_human_approval").default(true),
  
  // Monitoring
  lastRunAt: timestamp("last_run_at"),
  totalRuns: integer("total_runs").default(0),
  successfulRuns: integer("successful_runs").default(0),
  failedRuns: integer("failed_runs").default(0),
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_agent_config_type").on(table.agentType),
  index("idx_agent_config_active").on(table.isActive),
]);


// AI drafts table
export const aiDrafts = pgTable("ai_drafts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  draftType: varchar("draft_type").notNull(), // rebuttal, correspondence, report
  title: varchar("title").notNull(),
  content: text("content").notNull(),
  prompt: text("prompt"),
  approved: boolean("approved").default(false),
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
});


// AI Feature #2: Damage Assessments from Photos (Vision API)
export const damageAssessments = pgTable("damage_assessments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "cascade" }),
  
  // Photo analysis
  photoUrl: text("photo_url"),
  damageType: jsonb("damage_type"), // Array: water, fire, wind, hail, impact, structural, cosmetic
  severity: varchar("severity"), // minimal, moderate, severe, catastrophic
  affectedAreas: jsonb("affected_areas"), // Array of areas: roof, walls, ceiling, floor, etc.
  
  // AI Analysis
  description: text("description"), // Detailed AI-generated description
  estimatedRepairCost: decimal("estimated_repair_cost", { precision: 12, scale: 2 }),
  confidenceScore: decimal("confidence_score", { precision: 5, scale: 2 }), // 0-100%
  requiresExpertReview: boolean("requires_expert_review").default(false),
  
  // Materials detected
  materialsIdentified: jsonb("materials_identified"), // Array of materials detected
  damageIndicators: jsonb("damage_indicators"), // Specific damage indicators found
  
  // Recommendations
  recommendations: jsonb("recommendations"), // AI recommendations for documentation/action
  relatedDocuments: jsonb("related_documents"), // IDs of related photos/documents
  
  // Metadata
  isBeta: boolean("is_beta").default(true),
  modelUsed: varchar("model_used"), // gpt-4o-vision, gemini-vision, etc.
  processingTime: integer("processing_time"), // milliseconds
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_damage_assessments_claim_id").on(table.claimId),
  index("idx_damage_assessments_document_id").on(table.documentId),
  index("idx_damage_assessments_severity").on(table.severity),
]);


// AI Feature #5: Settlement Predictions & Negotiation Insights
export const settlementPredictions = pgTable("settlement_predictions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Prediction data
  predictedSettlement: decimal("predicted_settlement", { precision: 12, scale: 2 }),
  confidenceLevel: varchar("confidence_level"), // low, medium, high
  confidenceScore: decimal("confidence_score", { precision: 5, scale: 2 }), // 0-100%
  
  // Range estimates
  lowEstimate: decimal("low_estimate", { precision: 12, scale: 2 }),
  highEstimate: decimal("high_estimate", { precision: 12, scale: 2 }),
  
  // Analysis factors
  similarClaimsAnalyzed: integer("similar_claims_analyzed"),
  historicalDataPoints: integer("historical_data_points"),
  factorsConsidered: jsonb("factors_considered"), // Array of factors that influenced prediction
  
  // Negotiation insights
  negotiationStrategy: text("negotiation_strategy"),
  keyLeveragePoints: jsonb("key_leverage_points"), // Array of leverage points
  carrierTendencies: text("carrier_tendencies"), // Historical carrier behavior
  recommendedApproach: text("recommended_approach"),
  
  // Timing insights
  optimalNegotiationTiming: varchar("optimal_negotiation_timing"), // immediate, wait_for_more_data, after_inspection
  timeToSettlementEstimate: integer("time_to_settlement_estimate"), // days
  
  // Comparable claims
  comparableClaims: jsonb("comparable_claims"), // Array of similar claim references
  
  // Metadata
  modelUsed: varchar("model_used"),
  generatedAt: timestamp("generated_at").defaultNow(),
  isActive: boolean("is_active").default(true), // Latest prediction is active
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_settlement_predictions_claim_id").on(table.claimId),
  index("idx_settlement_predictions_is_active").on(table.isActive),
]);


// AI Feature #3 & #6: Smart Email Drafts and Task Generation
export const smartSuggestions = pgTable("smart_suggestions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Suggestion type
  suggestionType: varchar("suggestion_type").notNull(), // email_reply, task, deadline, follow_up
  
  // Content
  title: varchar("title"),
  content: text("content"),
  metadata: jsonb("metadata"), // Type-specific data
  
  // Context
  triggeredBy: varchar("triggered_by"), // communication_id, document_id, status_change, etc.
  contextData: jsonb("context_data"), // Relevant context that triggered suggestion
  
  // Priority and timing
  priority: varchar("priority"), // low, medium, high, urgent
  suggestedDueDate: timestamp("suggested_due_date"),
  
  // User action
  status: varchar("status").default("pending"), // pending, accepted, rejected, dismissed
  acceptedAt: timestamp("accepted_at"),
  rejectedReason: text("rejected_reason"),
  
  // AI metadata
  confidenceScore: decimal("confidence_score", { precision: 5, scale: 2 }),
  modelUsed: varchar("model_used"),
  
  createdBy: varchar("created_by"), // system or user ID
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_smart_suggestions_claim_id").on(table.claimId),
  index("idx_smart_suggestions_type").on(table.suggestionType),
  index("idx_smart_suggestions_status").on(table.status),
]);


// Universal AI Feedback Collection (for all AI features)
export const aiFeedback = pgTable("ai_feedback", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Feature identification
  featureType: varchar("feature_type").notNull(), // policy_analysis, damage_assessment, settlement_prediction, etc.
  entityId: uuid("entity_id").notNull(), // ID of the policy analysis, damage assessment, etc.
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  
  // Feedback data
  rating: integer("rating"), // 1-5 stars
  isHelpful: boolean("is_helpful"),
  feedbackType: varchar("feedback_type"), // positive, negative, correction, suggestion
  
  // Detailed feedback
  comment: text("comment"),
  specificIssues: jsonb("specific_issues"), // Array: inaccurate, incomplete, irrelevant, etc.
  
  // Rating dimensions
  accuracyRating: integer("accuracy_rating"), // 1-5
  relevanceRating: integer("relevance_rating"), // 1-5
  actionabilityRating: integer("actionability_rating"), // 1-5
  
  // Correction data (for training)
  correctedOutput: text("corrected_output"), // What the output should have been
  improvementSuggestions: text("improvement_suggestions"),
  
  // Training flag
  useForTraining: boolean("use_for_training").default(true),
  trainingDataExported: boolean("training_data_exported").default(false),
  
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_ai_feedback_feature_entity").on(table.featureType, table.entityId),
  index("idx_ai_feedback_claim_id").on(table.claimId),
  index("idx_ai_feedback_is_helpful").on(table.isHelpful),
  index("idx_ai_feedback_training").on(table.useForTraining, table.trainingDataExported),
]);


// Training Data Management
export const trainingData = pgTable("training_data", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Training set identification
  featureType: varchar("feature_type").notNull(),
  datasetName: varchar("dataset_name").notNull(),
  version: varchar("version").default("1.0"),
  
  // Training example
  input: text("input").notNull(), // The input that was given to AI
  expectedOutput: text("expected_output").notNull(), // The correct/desired output
  actualOutput: text("actual_output"), // What AI actually produced
  
  // Context and metadata
  contextData: jsonb("context_data"), // Additional context for this example
  sourceType: varchar("source_type"), // feedback_correction, manual_entry, successful_outcome
  sourceFeedbackId: uuid("source_feedback_id").references(() => aiFeedback.id),
  
  // Quality metrics
  qualityScore: decimal("quality_score", { precision: 5, scale: 2 }), // 0-100%
  isVerified: boolean("is_verified").default(false),
  verifiedBy: varchar("verified_by").references(() => users.id),
  verifiedAt: timestamp("verified_at"),
  
  // Training usage
  usedInTraining: boolean("used_in_training").default(false),
  trainingRunId: varchar("training_run_id"), // Reference to OpenAI fine-tuning job
  
  // Status
  status: varchar("status").default("pending"), // pending, approved, rejected, in_training, trained
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_training_data_feature").on(table.featureType),
  index("idx_training_data_status").on(table.status),
  index("idx_training_data_quality").on(table.qualityScore),
]);


// Fine-Tuning Job Management
export const fineTuningJobs = pgTable("fine_tuning_jobs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Job identification
  jobName: varchar("job_name").notNull(),
  featureType: varchar("feature_type").notNull(),
  openaiJobId: varchar("openai_job_id").unique(), // OpenAI fine-tuning job ID
  
  // Training details
  baseModel: varchar("base_model").notNull(), // gpt-4o-mini, gpt-4o, etc.
  trainingFileId: varchar("training_file_id"), // OpenAI file ID
  validationFileId: varchar("validation_file_id"), // OpenAI validation file ID
  
  // Training data statistics
  trainingExamplesCount: integer("training_examples_count"),
  validationExamplesCount: integer("validation_examples_count"),
  
  // Job status
  status: varchar("status").default("pending"), // pending, running, succeeded, failed, cancelled
  statusDetails: text("status_details"),
  
  // Results
  fineTunedModel: varchar("fine_tuned_model"), // The resulting model ID
  trainingMetrics: jsonb("training_metrics"), // Loss, accuracy, etc.
  
  // Performance tracking
  isActive: boolean("is_active").default(false), // Is this model currently in use?
  activatedAt: timestamp("activated_at"),
  performanceScore: decimal("performance_score", { precision: 5, scale: 2 }), // User feedback score
  
  // Cost tracking
  estimatedCost: decimal("estimated_cost", { precision: 10, scale: 2 }),
  actualCost: decimal("actual_cost", { precision: 10, scale: 2 }),
  
  startedBy: varchar("started_by").references(() => users.id).notNull(),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_fine_tuning_jobs_feature").on(table.featureType),
  index("idx_fine_tuning_jobs_status").on(table.status),
  index("idx_fine_tuning_jobs_active").on(table.isActive),
]);


export const discrepancyAnnotations = pgTable("discrepancy_annotations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Links to estimate sample
  estimateSampleId: uuid("estimate_sample_id").references(() => estimateSamples.id, { onDelete: "cascade" }).notNull(),
  
  // Discrepancy details
  pitfallPatternId: uuid("pitfall_pattern_id").references(() => estimatePitfallPatterns.id),
  customPitfallName: varchar("custom_pitfall_name"), // If it's not a standard pattern
  
  // Location in estimate
  lineItemReference: varchar("line_item_reference"), // Line number or description from estimate
  scopeArea: varchar("scope_area"), // roof, interior, HVAC, etc.
  
  // Financial impact
  carrierAmount: decimal("carrier_amount", { precision: 12, scale: 2 }),
  paAmount: decimal("pa_amount", { precision: 12, scale: 2 }),
  differenceDollars: decimal("difference_dollars", { precision: 12, scale: 2 }),
  differencePercentage: decimal("difference_percentage", { precision: 5, scale: 2 }),
  
  // Analysis
  issueDescription: text("issue_description").notNull(), // What's wrong with carrier's estimate
  correctApproach: text("correct_approach"), // How it should be estimated
  supportingEvidence: text("supporting_evidence"), // Why PA estimate is correct (photos, code, etc.)
  
  // Metadata
  severity: varchar("severity").default("medium"), // low, medium, high, critical
  wasRecovered: boolean("was_recovered").default(false), // Did carrier agree to this adjustment?
  recoveredAmount: decimal("recovered_amount", { precision: 12, scale: 2 }),
  
  annotatedBy: varchar("annotated_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_discrepancy_sample").on(table.estimateSampleId),
  index("idx_discrepancy_pattern").on(table.pitfallPatternId),
  index("idx_discrepancy_severity").on(table.severity),
]);


// Vertex AI Damage Training Dataset - Labeled photos for model training
export const damageTrainingDataset = pgTable("damage_training_dataset", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Image reference
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "cascade" }),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  imageUrl: text("image_url"), // URL to the image (could be from object storage or Drive)
  imagePath: text("image_path"), // File path if stored locally
  
  // Labels (ground truth annotations)
  damageTypes: jsonb("damage_types").notNull(), // Array: ["fire", "water", "wind", "hail", "impact", "structural", "cosmetic"]
  severity: varchar("severity").notNull(), // minimal, moderate, severe, catastrophic
  affectedAreas: jsonb("affected_areas"), // Array: ["roof", "walls", "ceiling", "floor", "foundation", "exterior"]
  materialsAffected: jsonb("materials_affected"), // Array: ["shingles", "drywall", "wood", "concrete", etc.]
  
  // Detailed annotations
  boundingBoxes: jsonb("bounding_boxes"), // Array of {x, y, width, height, label} for object detection
  segmentationMasks: jsonb("segmentation_masks"), // Pixel-level masks if available
  keyPoints: jsonb("key_points"), // Specific damage points with coordinates
  
  // Metadata
  perilType: perilTypeEnum("peril_type"), // Type of loss event
  estimatedCost: decimal("estimated_cost", { precision: 12, scale: 2 }), // Actual repair cost if known
  isVerified: boolean("is_verified").default(false), // Expert-verified label
  qualityScore: integer("quality_score"), // Image quality 1-10
  
  // Dataset management
  datasetSplit: varchar("dataset_split").default("unlabeled"), // train, validation, test, unlabeled
  usedForTraining: boolean("used_for_training").default(false),
  modelVersionsTrained: jsonb("model_versions_trained"), // Array of model IDs trained with this image
  
  // Labeling workflow
  labeledBy: varchar("labeled_by").references(() => users.id),
  verifiedBy: varchar("verified_by").references(() => users.id),
  labelingNotes: text("labeling_notes"),
  labelingTimeSeconds: integer("labeling_time_seconds"), // Time spent labeling
  
  // Status
  status: varchar("status").default("pending"), // pending, labeled, verified, rejected, in_review
  rejectionReason: text("rejection_reason"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_damage_training_dataset_claim").on(table.claimId),
  index("idx_damage_training_dataset_document").on(table.documentId),
  index("idx_damage_training_dataset_status").on(table.status),
  index("idx_damage_training_dataset_split").on(table.datasetSplit),
  index("idx_damage_training_dataset_verified").on(table.isVerified),
  index("idx_damage_training_dataset_peril").on(table.perilType),
]);


// Vertex AI Models - Track trained damage identification models
export const vertexAiModels = pgTable("vertex_ai_models", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Model identification
  modelName: varchar("model_name").notNull(),
  modelDisplayName: varchar("model_display_name").notNull(),
  modelVersion: varchar("model_version").notNull(), // v1.0, v1.1, etc.
  
  // Vertex AI specific
  vertexModelId: varchar("vertex_model_id"), // Vertex AI model resource ID
  vertexEndpointId: varchar("vertex_endpoint_id"), // Deployed endpoint ID
  gcpProjectId: varchar("gcp_project_id"), // Google Cloud project
  gcpRegion: varchar("gcp_region").default("us-central1"), // GCP region
  
  // Model architecture
  baseModel: varchar("base_model"), // EfficientNet, ViT, ResNet, etc.
  modelType: varchar("model_type").default("classification"), // classification, object_detection, segmentation
  architecture: jsonb("architecture"), // Detailed architecture config
  
  // Training details
  trainingDatasetSize: integer("training_dataset_size"), // Number of images used
  validationDatasetSize: integer("validation_dataset_size"),
  testDatasetSize: integer("test_dataset_size"),
  trainingStartedAt: timestamp("training_started_at"),
  trainingCompletedAt: timestamp("training_completed_at"),
  trainingDurationMinutes: integer("training_duration_minutes"),
  trainingCost: decimal("training_cost", { precision: 10, scale: 2 }), // USD cost
  
  // Performance metrics
  accuracy: decimal("accuracy", { precision: 5, scale: 2 }), // Overall accuracy %
  precision: decimal("precision", { precision: 5, scale: 2 }), // Precision %
  recall: decimal("recall", { precision: 5, scale: 2 }), // Recall %
  f1Score: decimal("f1_score", { precision: 5, scale: 2 }), // F1 score
  confusionMatrix: jsonb("confusion_matrix"), // Full confusion matrix
  perClassMetrics: jsonb("per_class_metrics"), // Metrics per damage type
  
  // Deployment status
  status: varchar("status").default("training"), // training, trained, deployed, archived, failed
  isActive: boolean("is_active").default(false), // Currently active model for predictions
  deployedAt: timestamp("deployed_at"),
  archivedAt: timestamp("archived_at"),
  
  // Model artifacts
  exportedModelPath: text("exported_model_path"), // Path to exported model container/weights
  containerImageUri: text("container_image_uri"), // Docker container URI
  modelCheckpointPath: text("model_checkpoint_path"), // Training checkpoint path
  
  // Training configuration
  hyperparameters: jsonb("hyperparameters"), // Learning rate, batch size, etc.
  augmentationConfig: jsonb("augmentation_config"), // Data augmentation settings
  classWeights: jsonb("class_weights"), // Weights for imbalanced classes
  
  // Usage tracking
  predictionCount: integer("prediction_count").default(0), // Number of predictions made
  averagePredictionTimeMs: integer("average_prediction_time_ms"), // Avg inference time
  lastUsedAt: timestamp("last_used_at"),
  
  // Notes and documentation
  description: text("description"),
  trainingNotes: text("training_notes"), // Notes about training process
  knownLimitations: text("known_limitations"), // Known issues or limitations
  improvementSuggestions: text("improvement_suggestions"),
  
  trainedBy: varchar("trained_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_vertex_models_name").on(table.modelName),
  index("idx_vertex_models_version").on(table.modelVersion),
  index("idx_vertex_models_status").on(table.status),
  index("idx_vertex_models_is_active").on(table.isActive),
  index("idx_vertex_models_vertex_id").on(table.vertexModelId),
]);


// AI Document Training Examples - Training examples for letters, rebuttals, DOI complaints
export const aiDocumentExamples = pgTable("ai_document_examples", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Template identification
  templateName: varchar("template_name").notNull(),
  templateType: varchar("template_type").notNull(), // 'formal_letter', 'doi_complaint', 'email_rebuttal', 'engineer_rebuttal', 'carrier_correspondence'
  category: varchar("category"), // Additional categorization
  
  // Content
  templateContent: text("template_content").notNull(), // The actual template text
  subject: varchar("subject"), // For email templates
  
  // Context and usage
  perilTypes: text("peril_types").array(), // Applicable peril types
  states: text("states").array(), // Applicable states
  situationDescription: text("situation_description"), // When to use this template
  keyPoints: text("key_points").array(), // Important points to include
  
  // Quality control and approval workflow
  submittedBy: varchar("submitted_by").references(() => users.id),
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  approvalStatus: varchar("approval_status").default("pending"), // pending, approved, rejected
  approvedAt: timestamp("approved_at"),
  rejectionReason: text("rejection_reason"),
  
  // Training metadata
  isApprovedForTraining: boolean("is_approved_for_training").default(false),
  usageCount: integer("usage_count").default(0), // How many times used
  effectivenessScore: integer("effectiveness_score"), // 0-100, based on feedback
  successRate: decimal("success_rate", { precision: 5, scale: 2 }), // % of successful outcomes
  
  // Source tracking
  sourceClaimId: uuid("source_claim_id").references(() => claims.id, { onDelete: "set null" }),
  sourceDocumentId: uuid("source_document_id").references(() => documents.id),
  
  // Tags and search
  tags: text("tags").array(),
  searchKeywords: text("search_keywords").array(),
  
  notes: text("notes"),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_ai_document_examples_type").on(table.templateType),
  index("idx_ai_document_examples_approval").on(table.approvalStatus),
  index("idx_ai_document_examples_training").on(table.isApprovedForTraining),
  index("idx_ai_document_examples_submitted_by").on(table.submittedBy),
]);


// Policy Classification Training - Teach AI to distinguish dec pages from full policies
export const policyClassificationTraining = pgTable("policy_classification_training", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Document information
  documentId: uuid("document_id").references(() => documents.id),
  documentPath: text("document_path").notNull(),
  fileName: varchar("file_name").notNull(),
  
  // Classification label
  classificationType: varchar("classification_type").notNull(), // 'declarations_page', 'full_policy'
  
  // Additional metadata for training
  carrier: varchar("carrier"),
  policyType: varchar("policy_type"), // HO3, HO6, DP3, etc.
  hasMultiplePages: boolean("has_multiple_pages"),
  pageCount: integer("page_count"),
  
  // Visual/structural features (for training)
  hasTabularData: boolean("has_tabular_data"), // Dec pages usually have tables
  hasCoverageAmounts: boolean("has_coverage_amounts"), // Dec pages show amounts
  hasLegalText: boolean("has_legal_text"), // Full policies have legal language
  hasExclusionsSection: boolean("has_exclusions_section"), // Full policies have exclusions
  
  // Quality control
  labeledBy: varchar("labeled_by").references(() => users.id),
  verifiedBy: varchar("verified_by").references(() => users.id),
  isVerified: boolean("is_verified").default(false),
  verifiedAt: timestamp("verified_at"),
  
  // Training dataset split
  datasetSplit: varchar("dataset_split").default("unlabeled"), // train, validation, test, unlabeled
  
  // Model confidence (after training)
  modelPrediction: varchar("model_prediction"), // What AI predicted
  predictionConfidence: decimal("prediction_confidence", { precision: 5, scale: 2 }), // 0-100
  isPredictionCorrect: boolean("is_prediction_correct"),
  
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_policy_classification_type").on(table.classificationType),
  index("idx_policy_classification_verified").on(table.isVerified),
  index("idx_policy_classification_split").on(table.datasetSplit),
  index("idx_policy_classification_labeled_by").on(table.labeledBy),
]);


export const aiInteractionLogs = pgTable("ai_interaction_logs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Interaction identification
  interactionType: aiInteractionTypeEnum("interaction_type").notNull(),
  featureType: varchar("feature_type").notNull(),
  
  // Context
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "set null" }),
  entityId: uuid("entity_id"),
  entityType: varchar("entity_type"),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "set null" }),

  // AI Input/Output
  aiInput: text("ai_input"),
  aiOutput: text("ai_output"),
  
  // User Response
  userAction: varchar("user_action"),
  userModifiedOutput: text("user_modified_output"),
  
  // Metrics
  responseTimeMs: integer("response_time_ms"),
  tokenCount: integer("token_count"),
  confidenceScore: decimal("confidence_score", { precision: 5, scale: 2 }),
  
  // Training weight factors (5-factor weighting system)
  // Formula: Weight = (Confidence × 0.25) + (ManagementInput × 0.30) + (OutcomeSuccess × 0.05) + (DataCompleteness × 0.30) + (PatternFrequency × 0.10)
  userFeedback: varchar("user_feedback"), // thumbs_up, thumbs_down, null
  managementWeight: decimal("management_weight", { precision: 5, scale: 2 }), // 1.0 for super_admin, 0.8 for owner/admin, 0.0 for others
  outcomeScore: decimal("outcome_score", { precision: 5, scale: 2 }), // Calculated when claim settles
  completenessScore: decimal("completeness_score", { precision: 5, scale: 2 }), // Context richness (docs, timeline, emails, transcriptions)
  patternScore: decimal("pattern_score", { precision: 5, scale: 2 }), // Matches successful patterns
  combinedWeight: decimal("combined_weight", { precision: 5, scale: 2 }), // Final calculated weight
  weightCalculatedAt: timestamp("weight_calculated_at"),
  
  // Training value
  isUsefulForTraining: boolean("is_useful_for_training").default(true),
  trainingExported: boolean("training_exported").default(false),
  exportedAt: timestamp("exported_at"),
  
  // Session tracking
  sessionId: varchar("session_id"),
  userId: varchar("user_id").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_ai_interaction_logs_type").on(table.interactionType),
  index("idx_ai_interaction_logs_feature").on(table.featureType),
  index("idx_ai_interaction_logs_claim").on(table.claimId),
  index("idx_ai_interaction_logs_user").on(table.userId),
  index("idx_ai_interaction_logs_training").on(table.isUsefulForTraining, table.trainingExported),
  index("idx_ai_interaction_logs_created").on(table.createdAt),
  index("idx_ai_interaction_logs_feedback").on(table.userFeedback),
  index("idx_ai_interaction_logs_combined_weight").on(table.combinedWeight),
  index("idx_ai_interaction_logs_org").on(table.orgId),
]);


export const aiTrainingFeedback = pgTable("ai_training_feedback", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Category and context
  category: aiTrainingFeedbackCategoryEnum("category").notNull(),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "set null" }),
  
  // Image support (stored in object storage)
  imageUrl: text("image_url"),
  imagePath: text("image_path"), // Object storage path
  
  // Text prompt/feedback from user
  userPrompt: text("user_prompt").notNull(),
  
  // What the AI produced (if applicable)
  aiResponse: text("ai_response"),
  
  // What the correct response should be (user correction)
  expectedResponse: text("expected_response"),
  
  // Additional context or notes
  contextNotes: text("context_notes"),
  
  // Priority and status
  priority: varchar("priority").default("normal"), // low, normal, high, critical
  status: varchar("status").default("pending"), // pending, reviewed, incorporated, rejected
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  
  // Training metadata
  isUsefulForTraining: boolean("is_useful_for_training").default(true),
  trainingExported: boolean("training_exported").default(false),
  exportedAt: timestamp("exported_at"),
  
  // Tracking
  submittedBy: varchar("submitted_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_ai_training_feedback_category").on(table.category),
  index("idx_ai_training_feedback_claim").on(table.claimId),
  index("idx_ai_training_feedback_status").on(table.status),
  index("idx_ai_training_feedback_user").on(table.submittedBy),
  index("idx_ai_training_feedback_training").on(table.isUsefulForTraining, table.trainingExported),
  index("idx_ai_training_feedback_created").on(table.createdAt),
]);


// AI Draft Edits - Track how users modify AI-generated content
export const aiDraftEdits = pgTable("ai_draft_edits", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Draft identification
  draftId: uuid("draft_id").references(() => aiDrafts.id, { onDelete: "cascade" }),
  draftType: varchar("draft_type").notNull(),
  
  // Context
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  
  // Original vs Modified content
  originalContent: text("original_content").notNull(),
  modifiedContent: text("modified_content").notNull(),
  
  // Edit analysis
  editDistance: integer("edit_distance"),
  percentageChanged: decimal("percentage_changed", { precision: 5, scale: 2 }),
  sectionsModified: jsonb("sections_modified"),
  
  // Specific changes
  addedContent: jsonb("added_content"),
  removedContent: jsonb("removed_content"),
  rephrasePatterns: jsonb("rephrase_patterns"),
  
  // Quality indicators
  wasApproved: boolean("was_approved"),
  approvalTime: integer("approval_time"),
  reviewerNotes: text("reviewer_notes"),
  
  // Edit reason capture - Learn WHY users made changes
  userReason: varchar("user_reason"), // User-selected reason category
  reasonCategory: varchar("reason_category"), // tone_adjustment, factual_correction, legal_compliance, missing_info, better_wording, terminology_fix, other
  customReason: text("custom_reason"), // Free-text explanation from user
  aiInferredReason: text("ai_inferred_reason"), // AI-analyzed reason from diff
  reasonConfidence: decimal("reason_confidence", { precision: 5, scale: 2 }), // AI confidence in inferred reason
  
  // Training weight factors
  userFeedback: varchar("user_feedback"), // thumbs_up, thumbs_down
  managementWeight: decimal("management_weight", { precision: 5, scale: 2 }),
  outcomeScore: decimal("outcome_score", { precision: 5, scale: 2 }),
  completenessScore: decimal("completeness_score", { precision: 5, scale: 2 }),
  patternScore: decimal("pattern_score", { precision: 5, scale: 2 }),
  combinedWeight: decimal("combined_weight", { precision: 5, scale: 2 }),
  weightCalculatedAt: timestamp("weight_calculated_at"),
  
  // Training value
  learningPriority: varchar("learning_priority").default("medium"),
  isUsefulForTraining: boolean("is_useful_for_training").default(true),
  trainingExported: boolean("training_exported").default(false),
  
  userId: varchar("user_id").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_ai_draft_edits_draft").on(table.draftId),
  index("idx_ai_draft_edits_type").on(table.draftType),
  index("idx_ai_draft_edits_claim").on(table.claimId),
  index("idx_ai_draft_edits_training").on(table.isUsefulForTraining, table.trainingExported),
  index("idx_ai_draft_edits_created").on(table.createdAt),
  index("idx_ai_draft_edits_reason").on(table.reasonCategory),
  index("idx_ai_draft_edits_weight").on(table.combinedWeight),
]);


// Timeline Note Patterns - Learn from timeline notes and communications
export const timelineNotePatterns = pgTable("timeline_note_patterns", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Source reference
  communicationId: uuid("communication_id").references(() => communications.id, { onDelete: "cascade" }),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "set null" }),

  // Note content and analysis
  noteContent: text("note_content").notNull(),
  noteType: varchar("note_type"),
  
  // Extracted patterns
  keyPhrases: jsonb("key_phrases"),
  actionItems: jsonb("action_items"),
  mentionedEntities: jsonb("mentioned_entities"),
  sentimentScore: decimal("sentiment_score", { precision: 5, scale: 2 }),
  
  // Context classification
  claimPhase: varchar("claim_phase"),
  communicationType: varchar("communication_type"),
  urgencyLevel: varchar("urgency_level"),
  
  // Learning indicators
  ledToPositiveOutcome: boolean("led_to_positive_outcome"),
  outcomeNotes: text("outcome_notes"),
  
  // Common phrase patterns
  phrasePatterns: jsonb("phrase_patterns"),
  responsePatterns: jsonb("response_patterns"),
  
  // Training value
  isUsefulForTraining: boolean("is_useful_for_training").default(true),
  trainingExported: boolean("training_exported").default(false),
  
  analyzedAt: timestamp("analyzed_at").defaultNow(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_timeline_note_patterns_claim").on(table.claimId),
  index("idx_timeline_note_patterns_comm").on(table.communicationId),
  index("idx_timeline_note_patterns_type").on(table.noteType),
  index("idx_timeline_note_patterns_phase").on(table.claimPhase),
  index("idx_timeline_note_patterns_training").on(table.isUsefulForTraining, table.trainingExported),
  index("idx_timeline_note_patterns_org").on(table.orgId),
]);


// Training Export Jobs - Track batch exports for model fine-tuning
export const trainingExportJobs = pgTable("training_export_jobs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Job identification
  jobName: varchar("job_name").notNull(),
  exportType: varchar("export_type").notNull(),
  
  // Data scope
  dataTypes: jsonb("data_types").notNull(),
  dateRangeStart: timestamp("date_range_start"),
  dateRangeEnd: timestamp("date_range_end"),
  filterCriteria: jsonb("filter_criteria"),
  
  // Export statistics
  recordsExported: integer("records_exported"),
  recordsSkipped: integer("records_skipped"),
  totalSize: integer("total_size"),
  
  // Output
  exportFormat: varchar("export_format").default("jsonl"),
  exportPath: text("export_path"),
  openaiFileId: varchar("openai_file_id"),
  
  // Status
  status: varchar("status").default("pending"),
  statusMessage: text("status_message"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  
  // Linked fine-tuning job
  fineTuningJobId: uuid("fine_tuning_job_id").references(() => fineTuningJobs.id),

  // The firm whose data the job exports (MIS-197). Set when the job is created, never changed.
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "set null" }),
  // Null for jobs the training scheduler starts (export_type scheduled_automatic / manual_force).
  startedBy: varchar("started_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_training_export_jobs_type").on(table.exportType),
  index("idx_training_export_jobs_status").on(table.status),
  index("idx_training_export_jobs_created").on(table.createdAt),
  index("idx_training_export_jobs_org").on(table.orgId),
]);


// Weekly Training Weight Snapshots - Track 100-week rolling average for AI training influence
// New data has 1% influence per week until 100 weeks of data is accumulated
export const weeklyTrainingSnapshots = pgTable("weekly_training_snapshots", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Week identification
  weekNumber: integer("week_number").notNull(), // Week 1-100+
  weekStartDate: timestamp("week_start_date").notNull(),
  weekEndDate: timestamp("week_end_date").notNull(),
  
  // Aggregate weights for this week
  totalInteractions: integer("total_interactions").default(0),
  avgConfidenceScore: decimal("avg_confidence_score", { precision: 5, scale: 2 }),
  avgManagementWeight: decimal("avg_management_weight", { precision: 5, scale: 2 }),
  avgOutcomeScore: decimal("avg_outcome_score", { precision: 5, scale: 2 }),
  avgCompletenessScore: decimal("avg_completeness_score", { precision: 5, scale: 2 }),
  avgPatternScore: decimal("avg_pattern_score", { precision: 5, scale: 2 }),
  avgCombinedWeight: decimal("avg_combined_weight", { precision: 5, scale: 2 }),
  
  // Record counts by type
  interactionCount: integer("interaction_count").default(0),
  draftEditCount: integer("draft_edit_count").default(0),
  searchCount: integer("search_count").default(0),
  documentAnalysisCount: integer("document_analysis_count").default(0),
  
  // Training influence calculation
  // Until week 100: all weights treated equally (1.0)
  // After week 100: this week contributes 1% to rolling average
  isBaselinePeriod: boolean("is_baseline_period").default(true), // True for weeks 1-99
  weeklyInfluence: decimal("weekly_influence", { precision: 5, scale: 4 }).default("0.01"), // 1% after week 100
  
  // Export tracking
  wasExported: boolean("was_exported").default(false),
  exportedAt: timestamp("exported_at"),
  exportJobId: uuid("export_job_id").references(() => trainingExportJobs.id),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_weekly_training_snapshots_week").on(table.weekNumber),
  index("idx_weekly_training_snapshots_dates").on(table.weekStartDate, table.weekEndDate),
  index("idx_weekly_training_snapshots_exported").on(table.wasExported),
]);


// Training Weight Configuration - System-wide settings for weight calculation
export const trainingWeightConfig = pgTable("training_weight_config", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Weight factor percentages (must sum to 1.0)
  confidenceWeight: decimal("confidence_weight", { precision: 5, scale: 2 }).default("0.25"), // 25%
  managementWeight: decimal("management_weight", { precision: 5, scale: 2 }).default("0.30"), // 30%
  outcomeWeight: decimal("outcome_weight", { precision: 5, scale: 2 }).default("0.05"), // 5%
  completenessWeight: decimal("completeness_weight", { precision: 5, scale: 2 }).default("0.30"), // 30%
  patternWeight: decimal("pattern_weight", { precision: 5, scale: 2 }).default("0.10"), // 10%
  
  // Baseline period settings
  baselineWeeks: integer("baseline_weeks").default(100), // Weeks 1-99 = baseline
  weeklyInfluenceAfterBaseline: decimal("weekly_influence_after_baseline", { precision: 5, scale: 4 }).default("0.01"), // 1%
  
  // Management role weights
  superAdminWeight: decimal("super_admin_weight", { precision: 5, scale: 2 }).default("1.00"),
  ownerWeight: decimal("owner_weight", { precision: 5, scale: 2 }).default("0.80"),
  adminWeight: decimal("admin_weight", { precision: 5, scale: 2 }).default("0.80"),
  otherRoleWeight: decimal("other_role_weight", { precision: 5, scale: 2 }).default("0.00"),
  
  isActive: boolean("is_active").default(true),
  updatedBy: varchar("updated_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


// AI Suggestion Feedback - Table for capturing user feedback on AI suggestions
export const aiSuggestionFeedback = pgTable("ai_suggestion_feedback", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Source identification
  sourceType: aiFeedbackSourceTypeEnum("source_type").notNull(),
  sourceId: varchar("source_id"), // Optional ID of the specific suggestion (e.g., claim ID, document ID)
  
  // Context
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  
  // User who provided feedback
  userId: varchar("user_id").references(() => users.id).notNull(),
  
  // Feedback data
  isHelpful: boolean("is_helpful").notNull(), // true = thumbs up, false = thumbs down
  correctionText: text("correction_text"), // Optional explanation/correction when not helpful
  
  // Original AI content for training context
  originalSuggestion: text("original_suggestion"), // The AI suggestion text that was rated
  
  // Metadata for training
  modelVersion: varchar("model_version"), // e.g., "v2.4.1"
  promptContext: jsonb("prompt_context"), // Additional context like state, claim type, etc.
  
  // Status for review workflow
  reviewedByAdmin: boolean("reviewed_by_admin").default(false),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  
  // Training export tracking
  exportedAt: timestamp("exported_at"),
  exportBatchId: varchar("export_batch_id"),
  
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_ai_feedback_source").on(table.sourceType, table.sourceId),
  index("idx_ai_feedback_claim").on(table.claimId),
  index("idx_ai_feedback_user").on(table.userId),
  index("idx_ai_feedback_helpful").on(table.isHelpful),
  index("idx_ai_feedback_created").on(table.createdAt),
]);


// Damage Analysis Corrections — Admin corrections to Vision AI photo analysis (feedback loop)
export const damageAnalysisCorrections = pgTable("damage_analysis_corrections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // References
  assessmentId: uuid("assessment_id").references(() => damageAssessments.id, { onDelete: "cascade" }).notNull(),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "set null" }),
  
  // Original AI output
  originalResult: jsonb("original_result").notNull(), // Full AI output (damageTypes, severity, areas, score)
  
  // Admin corrections
  correctedResult: jsonb("corrected_result").notNull(), // Admin's corrected values
  correctionFields: jsonb("correction_fields").$type<string[]>().notNull(), // Which fields were changed
  correctionNotes: text("correction_notes"), // Admin explanation of why AI was wrong
  correctionType: varchar("correction_type").notNull(), // correction, confirmation (positive reinforcement)
  
  // Context for future retrieval
  perilType: varchar("peril_type"), // Claim's peril type (for filtering)
  imageLabels: jsonb("image_labels"), // Vision API labels from original analysis
  
  // Embedding-based similarity search
  correctionEmbedding: text("correction_embedding"), // Serialized vector (1536 dims, text-embedding-3-small)
  analysisText: text("analysis_text"), // Original AI analysis text that was corrected (for display context)
  
  // Audit
  correctedBy: varchar("corrected_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_damage_corrections_assessment").on(table.assessmentId),
  index("idx_damage_corrections_claim").on(table.claimId),
  index("idx_damage_corrections_peril").on(table.perilType),
  index("idx_damage_corrections_type").on(table.correctionType),
]);


export const aiCorrections = pgTable("ai_corrections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // What feature produced this AI output
  feature: aiCorrectionFeatureEnum("feature").notNull(),
  correctionType: aiCorrectionTypeEnum("correction_type").notNull(),

  // Entity references
  entityId: uuid("entity_id"),           // ID of the assessment, estimate, draft, etc.
  entityType: varchar("entity_type"),     // "damage_assessment", "estimate", "draft", etc.
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "set null" }),

  // Core correction data (flexible JSONB)
  originalResult: jsonb("original_result").notNull(),
  correctedResult: jsonb("corrected_result").notNull(),
  correctionFields: jsonb("correction_fields").$type<string[]>().notNull(),
  correctionNotes: text("correction_notes"),

  // Feature-specific context for retrieval/matching
  // e.g. { perilType, imageLabels, coverageType, vendor, estimateSource }
  metadata: jsonb("metadata"),

  // Training export tracking
  isUsefulForTraining: boolean("is_useful_for_training").default(true),
  trainingExported: boolean("training_exported").default(false),
  exportedAt: timestamp("exported_at"),
  exportBatchId: varchar("export_batch_id"),

  // Audit
  correctedBy: varchar("corrected_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_ai_corrections_feature").on(table.feature),
  index("idx_ai_corrections_type").on(table.correctionType),
  index("idx_ai_corrections_claim").on(table.claimId),
  index("idx_ai_corrections_entity").on(table.entityId, table.entityType),
  index("idx_ai_corrections_training").on(table.isUsefulForTraining, table.trainingExported),
  index("idx_ai_corrections_created").on(table.createdAt),
]);

/**
 * LLM usage ledger (MIS-136): one row per provider call, written by
 * server/services/ai/llmSpend after the call with the provider's reported
 * tokens. cost_usd is the raw provider price from the price table in
 * llmSpend/config.ts. No markup here: the 1.54x is applied in billing later.
 * cost_estimated rows used a model missing from the price table, costed high
 * for the cap; billing must reprice them from the tokens.
 *
 * attribution 'firm' rows belong to org_id. 'unattributed' rows (MIS-200) are
 * AI calls with no firm (system work, a shared mailbox, an old workflow
 * input): org_id is null, they count toward no firm's cap, and only a
 * superadmin can read them (no firm's RLS policy matches them).
 */
export const LLM_USAGE_ATTRIBUTIONS = ['firm', 'unattributed'] as const;
export type LlmUsageAttribution = (typeof LLM_USAGE_ATTRIBUTIONS)[number];

export const llmUsageLedger = pgTable("llm_usage_ledger", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  attribution: varchar("attribution").$type<LlmUsageAttribution>().notNull().default('firm'),
  // No foreign key: a ledger row must never fail on the user it names.
  userId: varchar("user_id"),
  provider: varchar("provider").notNull(), // openai, gemini, litellm
  model: varchar("model").notNull(),
  feature: varchar("feature").notNull(), // AI feature key, or the client/adapter label
  route: varchar("route"), // e.g. "POST /api/ai/drafts", or a background job ("temporal createDraftForClaim")
  inputTokens: integer("input_tokens").notNull().default(0),
  outputTokens: integer("output_tokens").notNull().default(0),
  costUsd: decimal("cost_usd", { precision: 14, scale: 8 }).notNull(),
  costEstimated: boolean("cost_estimated").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_llm_usage_ledger_org_created").on(table.orgId, table.createdAt),
  index("idx_llm_usage_ledger_attribution_created").on(table.attribution, table.createdAt),
  check(
    "llm_usage_ledger_attribution_org_check",
    sql`(attribution = 'firm' AND org_id IS NOT NULL) OR (attribution = 'unattributed' AND org_id IS NULL)`,
  ),
  pgPolicy('llm_usage_ledger_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`attribution = 'firm' AND org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`attribution = 'firm' AND org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();

/**
 * Per-firm LLM limits (MIS-136). No row, or a null monthly_cap_usd, means the
 * LLM_FIRM_MONTHLY_CAP_USD default. kill_switch blocks every AI call for the
 * firm. A firm may read its own row but not change it.
 */
export const firmLlmLimits = pgTable("firm_llm_limits", {
  orgId: uuid("org_id").primaryKey().references(() => organizations.id, { onDelete: "cascade" }),
  monthlyCapUsd: decimal("monthly_cap_usd", { precision: 12, scale: 2 }),
  killSwitch: boolean("kill_switch").notNull().default(false),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  updatedBy: varchar("updated_by"),
}, () => [
  pgPolicy('firm_llm_limits_tenant_read', {
    as: 'permissive',
    for: 'select',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();

export type LlmUsageLedgerRow = typeof llmUsageLedger.$inferSelect;
export type InsertLlmUsageLedgerRow = typeof llmUsageLedger.$inferInsert;
export type FirmLlmLimitRow = typeof firmLlmLimits.$inferSelect;
